import React, { useState, useCallback, useEffect, useMemo, useRef, Fragment } from 'react';
import { Card, Button, Input, Progress } from 'components/ui';
import PhoneInput, { getCountryCallingCode } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import enLocale from 'react-phone-number-input/locale/en.json';
import { toast } from 'sonner';

// Create a reverse mapping of country names to codes
const countryNameToCode = Object.entries(enLocale).reduce((acc, [code, name]) => {
  acc[name.toLowerCase()] = code;
  return acc;
}, {});

// Helper to normalize phone numbers with + and country calling code
const getNormalizedPhoneNumber = (phoneVal, countryName = 'India') => {
  if (!phoneVal || typeof phoneVal !== 'string') {
    return phoneVal;
  }
  // Remove all non-numeric characters except +
  const cleaned = phoneVal.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) {
    return cleaned;
  }
  const countryCode = countryNameToCode[String(countryName).toLowerCase()] || 'IN';
  try {
    const callingCode = getCountryCallingCode(countryCode);
    if (cleaned.startsWith(callingCode)) {
      return `+${cleaned}`;
    } else {
      return `+${callingCode}${cleaned}`;
    }
  } catch (e) {
    console.warn("Could not get calling code for country:", countryName);
    return `+91${cleaned}`; // Default fallback to India's calling code +91
  }
};

import {
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  LockClosedIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { saveApplicationFieldProgress, submitApplicationById } from 'services/applicantService';
import ApplicationFormTemplate from './ApplicationFormTemplate';
import { getAccountantCounsellors, assignAccountantCounsellor } from 'services/leadsService';
import { contactService } from 'services/contactService';
import { getCompanyIdFromToken, getUserIdFromToken } from 'utils/tokenUtils';
import { ConfirmModal } from 'components/shared/ConfirmModal';
import { FilterDropdown } from 'components/leads/filters';

const ApplicationFormStage = ({
  applicationForm,
  leadUserId,
  applicationId,
  useCase = 'applicant',
  loading = false,
  onProgressUpdate,
  onFieldSaved,
  isFormSubmitted,
  progressData,
  companyName = '',
  leadDetails = null,
  leadData,
  onLeadDataRefresh,
}) => {
  const initialValues = useMemo(() => {
    if (!applicationForm?.Steps) return {};

    const values = {};
    
    // Determine country name first for phone number normalization
    const basicInfo = leadDetails?.BasicInformation || leadDetails?.basicInformation;
    const contactInfo = leadDetails?.ContactInformation || leadDetails?.contactInformation;
    let countryName = basicInfo?.Country || contactInfo?.Country || leadDetails?.country || 'India';
    
    applicationForm.Steps.forEach((step) => {
      step.Fields?.forEach((field) => {
        if (field.FieldName?.toLowerCase() === 'country' && field.Value) {
          countryName = field.Value;
        }
      });
    });

    applicationForm.Steps.forEach((step) => {
      step.Fields?.forEach((field) => {
        let value = field.Value;

        if ((value === null || value === undefined || value === '') && leadDetails) {
          const fieldName = field.FieldName?.toLowerCase();
          const fieldLabel = field.FieldLabel?.toLowerCase();

          if (fieldName === 'full_name' || fieldLabel?.includes('name')) {
            value = basicInfo?.FullName ||
              (basicInfo?.FirstName ? `${basicInfo.FirstName} ${basicInfo.LastName || ''}`.trim() : null);
          } else if (fieldName === 'email' || fieldLabel?.includes('email')) {
            value = basicInfo?.Email || contactInfo?.Email || leadDetails?.Email || leadDetails?.email || leadData?.email || leadData?.Email;
          } else if (fieldName === 'phone_number' || fieldLabel?.includes('phone') || fieldLabel?.includes('mobile')) {
            value = basicInfo?.PhoneNumber || contactInfo?.PhoneNumber || leadDetails?.PhoneNumber || leadDetails?.mobile || leadData?.mobile || leadData?.PhoneNumber;
          } else if (fieldName === 'country' || fieldLabel?.includes('country')) {
            value = basicInfo?.Country || contactInfo?.Country || leadDetails?.country || leadData?.country;
          } else if (fieldName === 'college' || fieldName === 'college_name' || fieldLabel?.includes('college')) {
            value = leadDetails?.CollegeId || leadDetails?.collegeId || leadData?.CollegeId || leadData?.collegeId || leadDetails?.college || leadDetails?.CollegeName || leadDetails?.College || leadData?.CollegeName || leadData?.College || leadData?.college;
          } else if (fieldName === 'stream' || fieldName === 'stream_name' || fieldLabel?.includes('stream')) {
            value = leadDetails?.StreamId || leadDetails?.streamId || leadData?.StreamId || leadData?.streamId || leadDetails?.stream || leadDetails?.StreamName || leadDetails?.Stream || leadData?.StreamName || leadData?.Stream || leadData?.stream;
          } else if (fieldName === 'semester' || fieldLabel?.includes('semester')) {
            value = leadDetails?.semester || leadDetails?.Semester || leadData?.Semester || leadData?.semester;
          } else if (fieldName === 'course' || fieldName === 'program' || fieldLabel?.includes('course') || fieldLabel?.includes('program')) {
            value = leadDetails?.course || leadDetails?.Course || leadDetails?.program || leadDetails?.Program || leadData?.Course || leadData?.Program || leadData?.course || leadData?.program;
          }
        }

        if (field.FieldTypeId === 10 && value) {
          value = getNormalizedPhoneNumber(String(value), countryName);
        }

        values[field.FieldId] = value;
      });
    });

    return values;
  }, [applicationForm, leadDetails]);

  const [formData, setFormData] = useState(initialValues);
  const [formDirty, setFormDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Counsellor selection state
  const [showCounsellorModal, setShowCounsellorModal] = useState(false);
  const [showConfirmSubmitModal, setShowConfirmSubmitModal] = useState(false);
  const [selectedCounsellor, setSelectedCounsellor] = useState('');
  const [counsellors, setCounsellors] = useState([]);
  const [isLoadingCounsellors, setIsLoadingCounsellors] = useState(false);

  // Static fields
  const [colleges, setColleges] = useState([]);
  const [streams, setStreams] = useState([]);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        const [collegesRes, streamsRes, sessionsRes] = await Promise.all([
          contactService.getColleges(),
          contactService.getStreams(),
          contactService.getSessions()
        ]);
        
        let parsedColleges = [];
        let parsedStreams = [];

        // Parse Colleges
        if (collegesRes?.statusCode === 200 || collegesRes?.status === 'Success') {
            const rawData = collegesRes.data;
            parsedColleges = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || []);
        } else if (Array.isArray(collegesRes)) {
            parsedColleges = collegesRes;
        } else if (collegesRes?.data) {
            parsedColleges = typeof collegesRes.data === 'string' ? JSON.parse(collegesRes.data) : collegesRes.data;
        }

        // Parse Streams
        if (streamsRes?.statusCode === 200 || streamsRes?.status === 'Success') {
            const rawData = streamsRes.data;
            parsedStreams = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || []);
        } else if (Array.isArray(streamsRes)) {
            parsedStreams = streamsRes;
        } else if (streamsRes?.data) {
            parsedStreams = typeof streamsRes.data === 'string' ? JSON.parse(streamsRes.data) : streamsRes.data;
        }

        // Parse Sessions
        let parsedSessions = [];
        if (sessionsRes?.statusCode === 200 || sessionsRes?.status === 'Success') {
            const rawData = sessionsRes.data;
            parsedSessions = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || []);
        } else if (Array.isArray(sessionsRes)) {
            parsedSessions = sessionsRes;
        } else if (sessionsRes?.data) {
            parsedSessions = typeof sessionsRes.data === 'string' ? JSON.parse(sessionsRes.data) : sessionsRes.data;
        }
        setSessions(parsedSessions);

        setColleges(parsedColleges);
        setStreams(parsedStreams);
      } catch (err) {
        console.error("Failed to fetch static data", err);
      }
    };
    fetchStaticData();
  }, []);

  useEffect(() => {
    const fetchCounsellors = async () => {
      setIsLoadingCounsellors(true);
      const res = await getAccountantCounsellors();
      if (res.success && Array.isArray(res.data)) {
        setCounsellors(res.data);
      }
      setIsLoadingCounsellors(false);
    };
    if (showCounsellorModal) {
      fetchCounsellors();
    }
  }, [showCounsellorModal]);

  const counsellorOptions = useMemo(() => {
    return counsellors.map((counsellor) => ({
      id: counsellor.UserId || counsellor.Id || counsellor.id,
      value: counsellor.UserId || counsellor.Id || counsellor.id,
      label: counsellor.FullName || counsellor.name || counsellor.AgentName
    }));
  }, [counsellors]);

  // ── Edit-mode state ──────────────────────────────────────────────────────────
  // Edit mode allows modifying fields even after submission/student conversion.


  useEffect(() => {
    setFormData(initialValues);
  }, [initialValues]);

  const [savingFields, setSavingFields] = useState({});

  const handleInputChange = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
    setFormDirty(true);
  };

  const isApplicationSubmitted = useMemo(() => {
    if (progressData && typeof progressData === 'object' && 'IsApplicationSubmitted' in progressData) {
      return Boolean(progressData.IsApplicationSubmitted);
    }
    return Boolean(isFormSubmitted);
  }, [isFormSubmitted, progressData]);

  const isApplicationFinalSubmitted = useMemo(() => {
    if (progressData && typeof progressData === 'object' && 'IsFinalSubmitted' in progressData) {
      return Boolean(progressData.IsFinalSubmitted);
    }
    return false;
  }, [progressData]);

  const academicCounsellor = leadDetails?.LeadAcademicCounsellorName || 
                            leadDetails?.SourceDetails?.LeadAcademicCounsellorName || 
                            leadData?.LeadAcademicCounsellorName || 
                            leadData?.SourceDetails?.LeadAcademicCounsellorName;

  const accountsCounsellor = leadDetails?.SourceDetails?.LeadAccountantCounsellorName || 
                            leadDetails?.accountsCounsellorName || 
                            leadData?.accountsCounsellorName || 
                            leadData?.SourceDetails?.LeadAccountantCounsellorName;

  const normalizedUseCase = (useCase || '').toLowerCase();
  const isApplicantView = normalizedUseCase === 'applicant';

  // Fields are read-only once a form is finally submitted, 
  // or if an accounts counsellor is assigned,
  // or if submitted and not in edit mode, or if leadUserId is missing
  const fieldsLocked =
    !leadUserId || !isEditing;

  const showCoordinatorActions = !isApplicantView;
  const showApplicantActions = isApplicantView && !isApplicationFinalSubmitted && !accountsCounsellor;

  const isFieldValueFilled = useCallback((field, rawValue) => {
    if (field.FieldTypeId === 5) {
      if (typeof rawValue === 'string') {
        return ['true', 'yes', '1', 'on'].includes(rawValue.trim().toLowerCase());
      }
      return Boolean(rawValue);
    }
    if (rawValue === undefined || rawValue === null || rawValue === '') return false;
    if (typeof rawValue === 'string') return rawValue.trim().length > 0;
    if (Array.isArray(rawValue)) return rawValue.length > 0;
    if (typeof rawValue === 'number') return !Number.isNaN(rawValue);
    if (typeof rawValue === 'boolean') return rawValue;
    return String(rawValue).trim().length > 0;
  }, []);

  const requiredFields = useMemo(() => {
    if (!applicationForm?.Steps) return [];
    const collected = [];
    applicationForm.Steps.forEach((step) => {
      step.Fields?.forEach((field) => {
        if (field.IsRequired) collected.push(field);
      });
    });
    return collected;
  }, [applicationForm]);

  const hasCompletedRequiredFields = useMemo(() => {
    if (!requiredFields.length) return true;
    return requiredFields.every((field) => isFieldValueFilled(field, formData[field.FieldId]));
  }, [requiredFields, formData, isFieldValueFilled]);

  const pdfExportRef = useRef(null);

  const handleGeneratePdf = useCallback(async () => {
    if (!pdfExportRef.current) {
      toast.error('Unable to generate PDF. Please try again.');
      return null;
    }
    try {
      const [html2canvasModule, jsPDFModule] = await Promise.all([
        import('html2canvas-pro'),
        import('jspdf'),
      ]);
      const html2canvas = html2canvasModule.default || html2canvasModule;
      const { jsPDF } = jsPDFModule;
      const canvas = await html2canvas(pdfExportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight, '', 'FAST');
      let heightLeft = imgHeight - pageHeight;
      let position = -pageHeight;
      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, '', 'FAST');
        heightLeft -= pageHeight;
        position -= pageHeight;
      }
      return pdf;
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
      return null;
    }
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    setIsGeneratingPdf(true);
    try {
      const pdf = await handleGeneratePdf();
      if (pdf) {
        const safeTitle = applicationForm?.Title || 'application-form';
        pdf.save(`${safeTitle.replace(/\s+/g, '-').toLowerCase()}-summary-${applicationId}.pdf`);
        toast.success('PDF downloaded successfully');
      }
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [applicationForm, applicationId, handleGeneratePdf]);

  const handlePreviewPdf = useCallback(async () => {
    setIsGeneratingPdf(true);
    try {
      const pdf = await handleGeneratePdf();
      if (pdf) setShowPreviewModal(true);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [handleGeneratePdf]);

  // ── Save (draft) ─────────────────────────────────────────────────────────────
  const handleSaveDraft = useCallback(async () => {
    if (!applicationId) {
      toast.error('Application ID not available');
      return;
    }
    setIsSaving(true);
    try {
      // Persist every dirty field in sequence
      const savePromises = Object.entries(formData).map(([fieldId, value]) => {
        if (!value?.toString().trim()) return Promise.resolve();
        const payload = { applicationId, fieldId, userId: leadUserId, value };
        return saveApplicationFieldProgress(payload).then((res) => {
          if (!res.success) toast.error(res.message || `Failed to save field ${fieldId}`);
        });
      });
      await Promise.all(savePromises);
      toast.success('Draft saved successfully');
      setFormDirty(false);
      if (onProgressUpdate) onProgressUpdate();
      if (onLeadDataRefresh) onLeadDataRefresh();
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, leadUserId, formData, onProgressUpdate]);

  // ── Final Submit ─────────────────────────────────────────────────────────────
  const handleSubmitApplication = useCallback(async () => {
    if (!applicationId) {
      toast.error('Application ID not available');
      return;
    }

    // If it's a final submit, we need an accountant counsellor
    if (isApplicationSubmitted && !selectedCounsellor) {
      toast.error('Please select an accounts counsellor');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isApplicationSubmitted) {
        // Final Submit Flow: Assign Accountant Counsellor first
        const assignmentPayload = {
          // leadDetails.id is the primary LeadId (GUID). leadUserId prop might be LeadUserId in some views.
          leadId: leadDetails?.id || leadDetails?.Id || leadData?.id || leadData?.LeadId || leadUserId,
          accountantCounsellorId: selectedCounsellor,
          companyId: getCompanyIdFromToken(),
          modifiedBy: getUserIdFromToken()
        };

        if (!assignmentPayload.leadId) {
          toast.error('Lead ID not found for assignment');
          setIsSubmitting(false);
          return;
        }

        const assignRes = await assignAccountantCounsellor(assignmentPayload);
        if (!assignRes.success) {
          toast.error(assignRes.error || 'Failed to assign accountant counsellor');
          setIsSubmitting(false);
          return;
        }
      }

      // Submit Application API call (hits /api/Applicant/SubmitApplicationById/)
      // This API handles setting IsSubmitted and IsFinalSubmitted based on the current state.
      const response = await submitApplicationById(applicationId);
      if (response.success) {
        toast.success(response.message || 'Application submitted successfully');
        setShowCounsellorModal(false);
        setIsEditing(false);
        if (onProgressUpdate) onProgressUpdate();
        if (onLeadDataRefresh) onLeadDataRefresh();
      } else {
        toast.error(response.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  }, [applicationId, onProgressUpdate, selectedCounsellor, isApplicationSubmitted, leadUserId, leadDetails, leadData]);

  // Auto-save field value on blur
  const handleFieldBlur = useCallback(async (fieldId, value) => {
    if (value === undefined || !value?.toString().trim()) return;
    setFormDirty(false);
    setSavingFields((prev) => ({ ...prev, [fieldId]: true }));
    try {
      const payload = { applicationId, fieldId, userId: leadUserId, value };
      const response = await saveApplicationFieldProgress(payload);
      if (response.success) {
        if (onFieldSaved) onFieldSaved(fieldId, value);
        toast.success('Field saved successfully');
        if (onProgressUpdate) onProgressUpdate();
      } else {
        toast.error(response.message || 'Failed to save field');
      }
    } catch (error) {
      console.error('Error saving field:', error);
      toast.error('Failed to save field');
    } finally {
      setSavingFields((prev) => {
        const updated = { ...prev };
        delete updated[fieldId];
        return updated;
      });
    }
  }, [applicationId, leadUserId, onProgressUpdate, onFieldSaved]);

  // ── Loading / empty states ───────────────────────────────────────────────────
  if (loading) {
    return (
      <Card className="mb-8">
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading application form...</p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!applicationForm) {
    return (
      <Card className="mb-8">
        <div className="p-6">
          <div className="text-center py-8">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No application form available</p>
          </div>
        </div>
      </Card>
    );
  }

  const { Title, Description, Steps = [] } = applicationForm;

  return (
    <>
      {/* Hidden PDF export target */}
      <div
        ref={pdfExportRef}
        className="pointer-events-none fixed left-[-9999px] top-[-9999px] z-[-1] w-[794px] bg-white text-gray-900"
      >
        <div className="p-8">
          <ApplicationFormTemplate
            applicationForm={applicationForm}
            formData={formData}
            companyName={companyName}
            showStepDescription
          />
        </div>
      </div>

      <Card className="mb-8">
        <div className="p-6">
          {/* ── Header ── */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {Title || 'Application Form'}
                </h3>
                {Description && Description !== 'Start building your custom form' && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">{Description}</p>
                )}
              </div>

              {/* ── Action buttons area ── */}
              <div className="flex flex-wrap items-center gap-3">
                {/* PDF download / preview — always visible once there's a form */}
                {/* <Button
                  variant="outlined"
                  color="neutral"
                  onClick={isApplicationSubmitted ? handleDownloadPdf : handlePreviewPdf}
                  disabled={isGeneratingPdf}
                >
                  {isGeneratingPdf
                    ? 'Preparing...'
                    : isApplicationSubmitted
                      ? 'Download PDF'
                      : 'Preview & Download'}
                </Button> */}



                {/* ── Submission actions ── */}
                {showCoordinatorActions && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      if (isEditing) {
                        if (!isApplicationSubmitted) {
                          handleSubmitApplication();
                        } else {
                          setIsEditing(false);
                        }
                      } else {
                        setIsEditing(true);
                      }
                    }}
                    disabled={isSubmitting || isGeneratingPdf}
                  >
                    {isSubmitting ? 'Saving...' : (isEditing ? 'Save' : 'Edit')}
                  </Button>
                )}
              </div>
            </div>



            {/* Application Progress Bar */}
            {/* <div className="mt-4 mb-2">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-gray-400">Application Progress</span>
                <span className="text-primary-600 dark:text-primary-400 font-semibold">
                  {progressData?.ApplicationCompletionPercentage
                    ? progressData.ApplicationCompletionPercentage.toFixed(1)
                    : '0.0'}%
                </span>
              </div>
              <Progress
                value={progressData?.ApplicationCompletionPercentage || 0}
                color="primary"
                className="h-2"
                showRail
              />
            </div> */}

            {!leadUserId && (
              <div className="mt-4 mb-2 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Please create a user profile for this lead first to enable editing the application fields.
                </p>
              </div>
            )}
          </div>

          {/* ── Form Steps ── */}
          <div className="space-y-8">
            {Steps.length === 0 ? (
              <div className="text-center py-8">
                <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No form fields available</p>
              </div>
            ) : (
              Steps.map((step, stepIndex) => (
                <div
                  key={stepIndex}
                  className="border-t border-gray-200 dark:border-gray-700 pt-6 first:border-t-0 first:pt-0"
                >
                  <div className="mb-6">
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                      {step.StepTitle}
                    </h4>
                    {step.StepDescription && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">{step.StepDescription}</p>
                    )}
                  </div>

                  <div className="space-y-4">
                    {step.Fields && step.Fields.length > 0 ? (
                      step.Fields.map((field) => (
                        <div key={field.FieldId} className="space-y-2">
                          <label className="block text-sm font-medium text-gray-900 dark:text-white">
                            {field.FieldLabel}
                            {field.IsRequired && <span className="text-red-500 ml-1">*</span>}
                          </label>

                          {/* TextBox / Number / Email / Password / Phone */}
                          {[1, 3, 8, 9, 10].includes(field.FieldTypeId) && (() => {
                            const isPhoneField = /phone|mobile|contact.*number/i.test(field.FieldLabel || '');

                            if (field.FieldTypeId === 10) {
                              const basicInfo = leadDetails?.BasicInformation || leadDetails?.basicInformation;
                              const contactInfo = leadDetails?.ContactInformation || leadDetails?.contactInformation;
                              let countryName = basicInfo?.Country || contactInfo?.Country || leadDetails?.country || 'India';
                              
                              // Check if there's a country value in formData
                              const countryField = Steps.flatMap(s => s.Fields || []).find(f => f.FieldName?.toLowerCase() === 'country');
                              if (countryField && formData[countryField.FieldId]) {
                                countryName = formData[countryField.FieldId];
                              }
                              
                              const countryCode = countryNameToCode[String(countryName).toLowerCase()] || 'IN';

                              return (
                                <div className="relative">
                                  <PhoneInput
                                    international
                                    country={countryCode}
                                    defaultCountry="IN"
                                    value={
                                      formData[field.FieldId] &&
                                        formData[field.FieldId].startsWith('+')
                                        ? formData[field.FieldId]
                                        : formData[field.FieldId]
                                          ? getNormalizedPhoneNumber(formData[field.FieldId], countryName)
                                          : ''
                                    }
                                    onChange={(value) => handleInputChange(field.FieldId, value || '')}
                                    onBlur={() => handleFieldBlur(field.FieldId, formData[field.FieldId])}
                                    placeholder={field.Placeholder || 'Enter phone number'}
                                    className="phone-input-custom"
                                    disabled={savingFields[field.FieldId] || fieldsLocked}
                                  />
                                  {savingFields[field.FieldId] && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            let inputType = 'text';
                            if (field.FieldTypeId === 8) inputType = 'email';
                            else if (field.FieldTypeId === 9) inputType = 'password';
                            else if (isPhoneField) inputType = 'tel';

                            const handlePhoneChange = (e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                              handleInputChange(field.FieldId, value);
                            };

                            const handlePhoneBlur = (e) => {
                              const value = e.target.value;
                              if (value && value.length !== 10) {
                                toast.error('Invalid phone number', {
                                  description: 'Please enter a valid 10-digit mobile number',
                                  duration: 3000,
                                });
                              }
                              handleFieldBlur(field.FieldId, value);
                            };

                            return (
                              <div className="relative">
                                <Input
                                  type={inputType}
                                  placeholder={field.Placeholder || `Enter ${field.FieldLabel.toLowerCase()}`}
                                  value={formData[field.FieldId] ?? ''}
                                  onChange={
                                    isPhoneField
                                      ? handlePhoneChange
                                      : (e) => handleInputChange(field.FieldId, e.target.value)
                                  }
                                  onBlur={
                                    isPhoneField
                                      ? handlePhoneBlur
                                      : (e) => handleFieldBlur(field.FieldId, e.target.value)
                                  }
                                  required={field.IsRequired}
                                  disabled={savingFields[field.FieldId] || fieldsLocked}
                                  className="w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  maxLength={isPhoneField ? 10 : undefined}
                                  pattern={isPhoneField ? '[0-9]{10}' : undefined}
                                />
                                {savingFields[field.FieldId] && (
                                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* TextArea */}
                          {field.FieldTypeId === 2 && (
                            <div className="relative">
                              <textarea
                                placeholder={field.Placeholder || `Enter ${field.FieldLabel.toLowerCase()}`}
                                value={formData[field.FieldId] ?? ''}
                                onChange={(e) => handleInputChange(field.FieldId, e.target.value)}
                                onBlur={(e) => handleFieldBlur(field.FieldId, e.target.value)}
                                required={field.IsRequired}
                                disabled={savingFields[field.FieldId] || fieldsLocked}
                                rows="4"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                              />
                              {savingFields[field.FieldId] && (
                                <div className="absolute right-3 top-3">
                                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                                </div>
                              )}
                            </div>
                          )}

                          {/* Date */}
                          {field.FieldTypeId === 4 && (
                            <div className="relative">
                              <Input
                                type="date"
                                value={formData[field.FieldId] ?? ''}
                                onChange={(e) => handleInputChange(field.FieldId, e.target.value)}
                                onBlur={(e) => handleFieldBlur(field.FieldId, e.target.value)}
                                required={field.IsRequired}
                                disabled={savingFields[field.FieldId] || fieldsLocked}
                                className="w-full"
                              />
                              {savingFields[field.FieldId] && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                                </div>
                              )}
                            </div>
                          )}

                          {/* Dropdown */}
                          {field.FieldTypeId === 7 && (
                            <select
                              value={formData[field.FieldId] ?? ''}
                              onChange={(e) => handleInputChange(field.FieldId, e.target.value)}
                              onBlur={(e) => handleFieldBlur(field.FieldId, e.target.value)}
                              required={field.IsRequired}
                              disabled={savingFields[field.FieldId] || fieldsLocked}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                            >
                              <option value="">Select {field.FieldLabel}</option>
                              {(() => {
                                const lowerLabel = (field.FieldLabel || '').toLowerCase();
                                let optionsToRender = field.Options || [];
                                
                                if (lowerLabel.includes('college')) {
                                  optionsToRender = colleges.map(c => ({ OptionValue: c.OptionLabel || c.CollegeName, OptionLabel: c.OptionLabel || c.CollegeName }));
                                } else if (lowerLabel.includes('stream')) {
                                  optionsToRender = streams.map(s => ({ OptionValue: s.StreamName, OptionLabel: s.StreamName }));
                                } else if (lowerLabel.includes('session')) {
                                  optionsToRender = sessions.map(s => ({ OptionValue: s.OptionValue || s.SessionName, OptionLabel: s.OptionLabel || s.SessionName }));
                                }

                                return optionsToRender.map((option, idx) => {
                                  const optionValue = typeof option === 'object' ? option.OptionValue : option;
                                  const optionLabel = typeof option === 'object' ? option.OptionLabel : option;
                                  return <option key={idx} value={optionValue}>{optionLabel}</option>;
                                });
                              })()}
                            </select>
                          )}

                          {/* Checkboxes */}
                          {field.FieldTypeId === 5 && field.Options && field.Options.length > 0 && (
                            <div className="space-y-2">
                              {field.Options.map((option, idx) => {
                                const optionValue = typeof option === 'object' ? option.OptionValue : option;
                                const optionLabel = typeof option === 'object' ? option.OptionLabel : option;
                                const isChecked = Array.isArray(formData[field.FieldId])
                                  ? formData[field.FieldId].includes(optionValue)
                                  : (formData[field.FieldId] || '').split(',').includes(optionValue);
                                return (
                                  <label key={idx} className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => {
                                        const currentValues = Array.isArray(formData[field.FieldId])
                                          ? formData[field.FieldId]
                                          : (formData[field.FieldId] || '').split(',').filter((v) => v);
                                        const newValues = e.target.checked
                                          ? [...currentValues, optionValue]
                                          : currentValues.filter((v) => v !== optionValue);
                                        handleInputChange(field.FieldId, newValues.join(','));
                                        handleFieldBlur(field.FieldId, newValues.join(','));
                                      }}
                                      disabled={savingFields[field.FieldId] || fieldsLocked}
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{optionLabel}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}

                          {/* Radio buttons */}
                          {field.FieldTypeId === 6 && field.Options && field.Options.length > 0 && (
                            <div className="space-y-2">
                              {field.Options.map((option, idx) => {
                                const optionValue = typeof option === 'object' ? option.OptionValue : option;
                                const optionLabel = typeof option === 'object' ? option.OptionLabel : option;
                                return (
                                  <label key={idx} className="flex items-center gap-2">
                                    <input
                                      type="radio"
                                      name={`field_${field.FieldId}`}
                                      value={optionValue}
                                      checked={(formData[field.FieldId] ?? '') === optionValue}
                                      onChange={(e) => handleInputChange(field.FieldId, e.target.value)}
                                      onBlur={(e) => handleFieldBlur(field.FieldId, e.target.value)}
                                      disabled={savingFields[field.FieldId] || fieldsLocked}
                                      className="w-4 h-4 border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{optionLabel}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No fields in this step</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Applicant bottom actions ── */}
          {showApplicantActions && (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {/* <Button
                variant="outlined"
                color="neutral"
                onClick={handlePreviewPdf}
                disabled={isGeneratingPdf}
              >
                {isGeneratingPdf ? 'Preparing...' : 'Preview & Download'}
              </Button> */}
              <div className="flex gap-3 sm:justify-end">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    if (isEditing) {
                      if (!isApplicationSubmitted) {
                        handleSubmitApplication();
                      } else {
                        setIsEditing(false);
                      }
                    } else {
                      setIsEditing(true);
                    }
                  }}
                  disabled={isSubmitting || isGeneratingPdf}
                >
                  {isSubmitting ? 'Saving...' : (isEditing ? 'Save' : 'Edit')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* ── PDF Preview Modal ── */}
      <Transition appear show={showPreviewModal} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-y-auto px-4 py-6 sm:px-5"
          onClose={() => { }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-2xl max-h-[90vh] origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 outline-none focus:outline-none">
              <div className="dark:bg-dark-800 flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5">
                <DialogTitle as="h3" className="dark:text-dark-100 text-base font-medium text-gray-800">
                  Application Form Preview
                </DialogTitle>
                <Button
                  onClick={() => setShowPreviewModal(false)}
                  variant="flat"
                  isIcon
                  className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
                >
                  <XMarkIcon className="size-4.5" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto bg-gray-100 dark:bg-gray-800 p-4 sm:p-6">
                <div className="mx-auto w-full max-w-[794px] bg-white">
                  <div className="p-8 break-words">
                    <ApplicationFormTemplate
                      applicationForm={applicationForm}
                      formData={formData}
                      companyName={companyName}
                    />
                  </div>
                </div>
              </div>

              <div className="dark:bg-dark-700 flex justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-5 dark:border-gray-700">
                <Button
                  onClick={() => setShowPreviewModal(false)}
                  variant="outlined"
                  className="min-w-[7rem] rounded-full"
                  disabled={isGeneratingPdf}
                >
                  Close
                </Button>
                <Button
                  color="primary"
                  onClick={async () => {
                    await handleDownloadPdf();
                    setShowPreviewModal(false);
                  }}
                  className="min-w-[7rem] rounded-full"
                  disabled={isGeneratingPdf}
                >
                  {isGeneratingPdf ? 'Downloading...' : 'Download PDF'}
                </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      {/* ── Counsellor Selection Modal ── */}
      <Transition appear show={showCounsellorModal} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-y-auto px-4 py-6 sm:px-5"
          onClose={() => setShowCounsellorModal(false)}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-md origin-top flex-col overflow-visible rounded-lg bg-white transition-all duration-300 outline-none focus:outline-none">
              <div className="dark:bg-dark-800 flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5">
                <DialogTitle as="h3" className="dark:text-dark-100 text-base font-medium text-gray-800">
                  Assign Accounts Counsellor
                </DialogTitle>
                <Button
                  onClick={() => setShowCounsellorModal(false)}
                  variant="flat"
                  isIcon
                  className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
                >
                  <XMarkIcon className="size-4.5" />
                </Button>
              </div>
              <div className="p-4 sm:p-6 bg-white dark:bg-gray-800">

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Please select an accounts counsellor to assign for this application.
                </p>
                <div className="space-y-3">
                  <FilterDropdown
                    filterKey="accountantCounsellor"
                    options={counsellorOptions}
                    selectedValue={selectedCounsellor}
                    onSelect={(key, value) => {
                      if (value !== 'all') {
                        setSelectedCounsellor(value);
                      }
                    }}
                    onClear={() => setSelectedCounsellor('')}
                    placeholder="Select a Accounts User"
                    showAllOption={false}
                    showSearch={true}
                    searchThreshold={0}
                    className="w-full"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="dark:bg-dark-700 flex justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-5 dark:border-gray-700">
                <Button
                  onClick={() => setShowCounsellorModal(false)}
                  variant="outlined"
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={handleSubmitApplication}
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting || !selectedCounsellor}
                >
                  {isSubmitting ? 'Submitting...' : 'Assign & Submit'}
                </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
      {/* ── Final Submission Confirmation Modal ── */}
      <ConfirmModal
        show={showConfirmSubmitModal}
        state="pending"
        onClose={() => setShowConfirmSubmitModal(false)}
        onOk={() => {
          setShowConfirmSubmitModal(false);
          setShowCounsellorModal(true);
        }}
        confirmLoading={isSubmitting}
        messages={{
          pending: {
            title: "Confirm Final Submission",
            description: "Are you sure you want to submit the application finally? This action will lock the application for further edits. You will be prompted to select an accounts counsellor to complete the process.",
            actionText: "Yes, Proceed",
          }
        }}
        buttonColor="primary"
      />
    </>
  );
};

export default ApplicationFormStage;
